// src/services/auth.service.ts
import { authApi } from "./api"; // Feltételezzük, hogy az authApi helyesen kezeli a kéréseket
import api from "./api";

// Importáljuk a központi típusokat
import {
  User,
  CustomerRegisterData,
  ShopRegisterData,
} from "../types/index"; // Import a központi definícióból

// Az AuthResponse most már a központi User típust használja
export interface AuthResponse {
  token: string;
  user: User; // Itt a központi, frissített User típusra hivatkozunk
}

// Profilkép válasz (maradhat)
export interface ProfilePictureResponse {
  imageUrl: string;
  success: boolean;
}

// Teljes felhasználói profil (maradhat, de lehetne a User-t kiterjeszteni)
// Alternatíva: Lehetne `UserProfile extends User` a types/index.ts-ben
export interface UserProfile extends User {
  phoneNumber?: string;
  notifications?: boolean;
  publicProfile?: boolean;
  createdAt?: string;
  updatedAt?: string;
  // A User típusból örökli az id, name, email, role, shopId stb. mezőket
}

class AuthService {
  // Bejelentkezés
  async login(email: string, password: string): Promise<AuthResponse | null> {
    try {
      console.log("[AuthService.login] Attempting API login...");
      const response = await authApi.login({ email, password });
      if (response?.data?.token && response?.data?.user) {
        const authData: AuthResponse = response.data;
        console.log(
          "[AuthService.login] Login API call successful. Response user data:",
          authData.user
        );
        console.log("[AuthService.login] Token from API:", authData.token);

        this.setToken(authData.token);
        this.setUser(authData.user);
        this.setAuthHeader(authData.token); // Ez beállítja az axios.defaults...

        return authData;
      }
      console.warn(
        "[AuthService.login] Invalid login response structure from API",
        response?.data
      );
      return null;
    } catch (error: any) {
      console.error(
        "[AuthService.login] Login API error:",
        error.response?.data || error.message,
        error
      );
      return null;
    }
  }

  // Felhasználói adatok frissítése
  async updateUserProfile(
    userId: string,
    userData: Partial<User>
  ): Promise<User | null> {
    // Visszatérhet a frissített Userrel
    try {
      const response = await api.put(`/auth/users/${userId}`, userData);
      if (response?.data) {
        // Itt a backend várhatóan a teljes, frissített user objektumot adja vissza
        const updatedUser: User = response.data;
        this.setUser(updatedUser); // Frissítjük a localStorage-ban
        // Az AuthContext state-jét is frissíteni kellene! (Ezt az updateUser az AuthContext-ben hívja meg)
        return updatedUser;
      }
      return null;
    } catch (error: any) {
      console.error(
        "AuthService: Update user profile error:",
        error.response?.data || error.message
      );
      return null;
    }
  }

  // Teljes felhasználói profil lekérése
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const response = await api.get(`/auth/users/${userId}/profile`);
      // Itt UserProfile típust várunk vissza
      if (response?.data) {
        return response.data as UserProfile; // Típuskényszerítés
      }
      return null;
    } catch (error: any) {
      console.error(
        "AuthService: Get user profile error:",
        error.response?.data || error.message
      );
      return null;
    }
  }

  // Profilkép feltöltése
  async uploadProfilePicture(
    userId: string,
    formData: FormData
  ): Promise<ProfilePictureResponse | null> {
    try {
      const response = await api.post(
        `/auth/users/${userId}/profile-picture`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response?.data?.success && response?.data?.imageUrl) {
        const profileResponse: ProfilePictureResponse = response.data;
        // Frissítjük a tárolt felhasználó képét, ha van felhasználó
        const currentUser = this.getUser();
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            profileImage: profileResponse.imageUrl,
          };
          this.setUser(updatedUser); // Csak a localStorage-t frissíti itt! Az AuthContext-et máshol kell.
        }
        return profileResponse;
      }
      return null;
    } catch (error: any) {
      console.error(
        "AuthService: Upload profile picture error:",
        error.response?.data || error.message
      );
      return null;
    }
  }

  // Vásárló regisztráció
  async registerCustomer(data: CustomerRegisterData): Promise<boolean> {
    try {
      const response = await authApi.registerCustomer(data);
      // A !! konvertálja a választ boolean-re (true, ha van adat, false, ha nincs)
      return !!response?.data;
    } catch (error: any) {
      console.error(
        "AuthService: Customer registration error:",
        error.response?.data || error.message
      );
      return false;
    }
  }

  // Bolt regisztráció
  async registerShop(data: ShopRegisterData): Promise<boolean> {
    try {
      const response = await authApi.registerShop(data);
      return !!response?.data;
    } catch (error: any) {
      console.error(
        "AuthService: Shop registration error:",
        error.response?.data || error.message
      );
      return false;
    }
  }

  // Kijelentkezés
  logout(): void {
    console.log(
      "[AuthService.logout] Attempting to remove auth_token and auth_user from localStorage."
    );
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    delete api.defaults.headers.common["Authorization"]; // Töröljük a default headert is
    console.log(
      "AuthService: User logged out, local storage cleared, default header removed."
    );
  }

  // JWT token lekérése
  getToken(): string | null {
    const token = localStorage.getItem("auth_token");
    console.log(
      "[AuthService.getToken] Retrieved token from localStorage:",
      token
    );
    return token;
  }

  // JWT token beállítása (localStorage)
  setToken(token: string): void {
    try {
      console.log(
        "[AuthService.setToken] Attempting to save token to localStorage. Token:",
        token
      );
      localStorage.setItem("auth_token", token);
      const retrievedToken = localStorage.getItem("auth_token");
      console.log(
        "[AuthService.setToken] Token SAVED to localStorage. Value now in localStorage:",
        retrievedToken,
        "(Should match token above)"
      );
      if (token !== retrievedToken) {
        console.error(
          "[AuthService.setToken] CRITICAL: Token retrieved from localStorage MISMATCHES token saved!"
        );
      }
    } catch (error) {
      console.error("AuthService: Error setting token in localStorage", error);
    }
  }

  // Felhasználó adatainak lekérése (localStorage)
  getUser(): User | null {
    const userStr = localStorage.getItem("auth_user");
    console.log(
      "[AuthService.getUser] Retrieved user string from localStorage:",
      userStr
    );
    if (userStr) {
      try {
        return JSON.parse(userStr) as User;
      } catch (error) {
        console.error(
          "AuthService: Error parsing user from localStorage",
          error
        );
        localStorage.removeItem("auth_user");
        return null;
      }
    }
    return null;
  }

  // Felhasználó adatainak beállítása (localStorage)
  setUser(user: User): void {
    try {
      console.log("[AuthService.setUser] Saving user to localStorage:", user);
      localStorage.setItem("auth_user", JSON.stringify(user));
    } catch (error) {
      console.error("AuthService: Error setting user in localStorage", error);
    }
  }
  // Auth header beállítása az 'api' példányhoz
  setAuthHeader(token: string | null): void {
    if (token) {
      // Ez a sor már nem feltétlenül szükséges, ha az interceptor jól működik,
      // de nem árt, ha a default is be van állítva.
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      console.log(
        "[AuthService.setAuthHeader] Axios DEFAULT Authorization header SET:",
        api.defaults.headers.common["Authorization"]
      );
    } else {
      delete api.defaults.headers.common["Authorization"];
      console.log(
        "[AuthService.setAuthHeader] Axios DEFAULT Authorization header REMOVED."
      );
    }
  }

  // Token érvényesség ellenőrzése (kliens oldali)
  isTokenValid(): boolean {
    const token = this.getToken(); // Ez már logol
    if (!token) {
      console.log("[AuthService.isTokenValid] No token found.");
      return false;
    }

    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        console.log(
          "[AuthService.isTokenValid] Token is not a valid JWT structure (not 3 parts)."
        );
        return false;
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = atob(base64);
      const payload = JSON.parse(jsonPayload);
      console.log("[AuthService.isTokenValid] Decoded payload:", payload);

      if (!payload.exp) {
        console.log(
          "[AuthService.isTokenValid] Token has no expiration (exp claim), considering valid."
        );
        return true;
      }

      const now = Math.floor(Date.now() / 1000);
      const isValid = payload.exp > now;
      console.log(
        `[AuthService.isTokenValid] Token expiration check: exp=${payload.exp
        } (${new Date(payload.exp * 1000)}), now=${now} (${new Date(
          now * 1000
        )}), isValid=${isValid}`
      );
      return isValid;
    } catch (error) {
      console.error(
        "[AuthService.isTokenValid] Token validation error (atob, JSON.parse, or other):",
        error,
        "Problematic Token was:",
        token
      );
      return false;
    }
  }
  // auth.service.ts - javított verifyEmail metódus
  async verifyEmail(token: string): Promise<boolean> {
    try {
      const response = await api.get(`/auth/verify?token=${token}`);
      return response?.data?.success === true;
    } catch (error: any) {
      console.error(
        "AuthService: Email verification error:",
        error.response?.data || error.message
      );
      return false;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    // Visszatérési érték lehet void, vagy specifikusabb
    try {
      const response = await api.post("/auth/forgot-password", { email });
      // A backend általában 200 OK-t ad, még ha az email nem is létezik,
      // hogy ne lehessen kitalálni a regisztrált címeket.
      console.log("Forgot password request sent:", response.data);
      // Nem kell itt semmit visszaadni, a komponens kezeli az üzenetet.
    } catch (error: any) {
      console.error(
        "AuthService - Forgot Password error:",
        error.response?.data || error.message
      );
      // Dobjuk tovább a hibát, hogy a komponens elkapja és kezelje
      throw error;
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      const response = await api.post("/auth/reset-password", {
        token,
        newPassword,
      });
      console.log("Reset password request successful:", response.data);
    } catch (error: any) {
      console.error(
        "AuthService - Reset Password error:",
        error.response?.data || error.message
      );
      throw error;
    }
  }

  // ==================== KEDVENCEK ====================

  // Kedvenc bolt toggle (hozzáadás/eltávolítás)
  async toggleFavoriteShop(shopId: string): Promise<{ isFavorite: boolean; favoriteShops: string[] } | null> {
    try {
      const response = await api.post(`/auth/favorites/${shopId}`);
      return response.data;
    } catch (error: any) {
      console.error('[AuthService.toggleFavoriteShop] Error:', error.response?.data || error.message);
      throw error;
    }
  }

  // Kedvenc boltok lekérése
  async getFavoriteShops(): Promise<any[]> {
    try {
      const response = await api.get('/auth/favorites');
      return response.data.favoriteShops || [];
    } catch (error: any) {
      console.error('[AuthService.getFavoriteShops] Error:', error.response?.data || error.message);
      return [];
    }
  }
}

// Létrehozunk egy példányt és azt exportáljuk (Singleton minta)
const authServiceInstance = new AuthService();
export default authServiceInstance;

// Gondoskodjunk róla, hogy az app indulásakor beállítódjon a header, ha van token
const initialToken = authServiceInstance.getToken();
if (initialToken && authServiceInstance.isTokenValid()) {
  authServiceInstance.setAuthHeader(initialToken);
}
